import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  UseCase,
  ActionDetail,
  StepActionParameter,
  ParameterValue,
  ReviewStatus,
  UseCaseStep,
} from "../../types/TestTarget";
import { Add as AddIcon, KeyboardArrowUp as ArrowUpIcon, KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import {
  getActionConfig,
  getAvailableActions,
  hasTestTargetParameter,
  validateRequiredParameters,
} from "../../utils/actionConfig";
import { uuidv7 } from "uuidv7";
import { ViewValue, StatusBadge, InvalidTargetBadge, TargetWarningText } from "./styles";
import { BaseStepsList } from "./BaseStepsList";
import { ParameterSection } from "./ParameterSection";
import { STATUS_LABELS } from "../../utils/statusUtils";
import {
  get_target_count_status,
  get_target_warning_messages,
  has_invalid_targets,
} from "../../utils/targetValidation";

interface PolicyTabProps {
  editedUseCase: UseCase;
  setEditedUseCase: React.Dispatch<React.SetStateAction<UseCase | null>>;
  mode: "view" | "edit" | "create";
  localTestTargets: any[];
  currentViewId?: string;
  initialPolicyId?: string;
  onAddPolicy: () => void;
  onRemovePolicy: (index: number) => void;
  onUpdatePolicy: (
    index: number,
    field: "name" | "description" | "status",
    value: string | ReviewStatus
  ) => void;
  onAddPolicyStep: (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps"
  ) => void;
  onRemovePolicyStep: (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number
  ) => void;
  onUpdatePolicyStep: (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number,
    field: string,
    value: string | ActionDetail
  ) => void;
  onCreateTestTarget?: (callback?: (newTarget: any) => void) => void;
  onAddPolicyParameter: (policyIndex: number) => void;
  onRemovePolicyParameter: (policyIndex: number, parameterIndex: number) => void;
  onUpdatePolicyParameter: (
    policyIndex: number,
    parameterIndex: number,
    field: keyof ParameterValue,
    value: string
  ) => void;
  policyParameterErrors?: Map<string, string>;
  policyStepValidationErrors?: Map<string, string>;
}

export const PolicyTab: React.FC<PolicyTabProps> = ({
  editedUseCase,
  setEditedUseCase,
  mode,
  localTestTargets,
  currentViewId,
  initialPolicyId,
  onAddPolicy,
  onRemovePolicy,
  onUpdatePolicy,
  onAddPolicyStep,
  onRemovePolicyStep,
  onUpdatePolicyStep,
  onCreateTestTarget,
  onAddPolicyParameter,
  onRemovePolicyParameter,
  onUpdatePolicyParameter,
  policyParameterErrors = new Map(),
  policyStepValidationErrors = new Map(),
}) => {
  // initialPolicyIdが指定されている場合は、そのポリシーのインデックスを初期選択
  const getInitialPolicyIndex = () => {
    if (initialPolicyId && editedUseCase.policies) {
      const index = editedUseCase.policies.findIndex(
        (policy) => policy.policy_id === initialPolicyId
      );
      if (index !== -1) return index;
    }
    return editedUseCase.policies && editedUseCase.policies.length > 0 ? 0 : null;
  };

  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState<number | null>(
    getInitialPolicyIndex()
  );
  const [activeSubTab, setActiveSubTab] = useState<"info" | "steps">("info");

  // initialPolicyIdが変更されたときにselectedPolicyIndexをリセット
  useEffect(() => {
    if (initialPolicyId && editedUseCase.policies) {
      const index = editedUseCase.policies.findIndex(
        (policy) => policy.policy_id === initialPolicyId
      );
      if (index !== -1) {
        setSelectedPolicyIndex(index);
      }
    }
  }, [initialPolicyId, editedUseCase.policies]);

  // ステップ単位で不正なターゲットをチェックする関数
  // ステップのtest_targetsに含まれるtest_target_idが実際に存在するかチェック
  // NOTE: localTestTargetsを使用することで、新規作成されたターゲットも含めて判定する
  const hasInvalidTargetsInStep = useCallback(
    (step: UseCaseStep): boolean =>
      has_invalid_targets(step.action_detail?.test_targets, localTestTargets),
    [localTestTargets]
  );

  const get_target_warning_state = useCallback(
    (
      step: UseCaseStep
    ): {
      has_target_count_mismatch: boolean;
      target_warning_messages: string[];
    } => {
      const count_status = get_target_count_status(
        step.action_detail?.step_action,
        step.action_detail?.test_targets
      );
      return {
        has_target_count_mismatch: count_status?.has_mismatch ?? false,
        target_warning_messages: get_target_warning_messages(
          step.action_detail?.step_action,
          step.action_detail?.test_targets,
          localTestTargets
        ),
      };
    },
    [localTestTargets]
  );

  const render_target_warning_messages = (
    target_warning_messages: string[],
    key_prefix: string
  ): React.ReactNode => {
    if (target_warning_messages.length === 0) {
      return null;
    }
    return (
      <TargetWarningText>
        {target_warning_messages.map((message, warning_idx) => (
          <div key={`${key_prefix}-${warning_idx}`}>{message}</div>
        ))}
      </TargetWarningText>
    );
  };

  // 選択されたポリシーで使用されているパラメータを抽出
  const usedPolicyParameters = useMemo(() => {
    if (!editedUseCase.policies || selectedPolicyIndex === null) return new Set<string>();
    
    const selectedPolicy = editedUseCase.policies[selectedPolicyIndex];
    if (!selectedPolicy) return new Set<string>();
    
    const params = new Set<string>();
    
    const allSteps = [
      ...selectedPolicy.changes.add_steps,
      ...selectedPolicy.changes.update_steps,
    ];
    
    allSteps.forEach((step) => {
      // ステップ文字列から{{param}}形式のパラメータを抽出
      if (step.step) {
        const stepParams = step.step.match(/\{\{([^}]+)\}\}/g);
        if (stepParams) {
          stepParams.forEach((p) => {
            const paramName = p.replace(/\{\{|\}\}/g, "");
            params.add(paramName);
          });
        }
      }
      
      // 期待結果からも抽出
      if (step.expect) {
        const expectParams = step.expect.match(/\{\{([^}]+)\}\}/g);
        if (expectParams) {
          expectParams.forEach((p) => {
            const paramName = p.replace(/\{\{|\}\}/g, "");
            params.add(paramName);
          });
        }
      }

      // action_detailのparametersからも抽出
      if (step.action_detail?.step_action?.parameters) {
        step.action_detail.step_action.parameters.forEach((param) => {
          // parameterのvalueから{{param}}形式のパラメータを抽出
          if (param.value) {
            const actionParams = param.value.match(/\{\{([^}]+)\}\}/g);
            if (actionParams) {
              actionParams.forEach((p) => {
                const paramName = p.replace(/\{\{|\}\}/g, "");
                params.add(paramName);
              });
            }
          }
        });
      }
    });
    
    return params;
  }, [editedUseCase.policies, selectedPolicyIndex]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* 上部：ポリシータブ */}
      <div
        style={{
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
          padding: "16px 16px 0 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: "600",
              color: "#1e293b",
            }}
          >
            ポリシー ({editedUseCase.policies?.length || 0})
          </h3>
          {mode === "edit" && (
            <button
              onClick={() => {
                onAddPolicy();
                // 新しく追加されるポリシーを選択状態にする
                const newIndex = (editedUseCase.policies?.length || 0);
                setSelectedPolicyIndex(newIndex);
              }}
              style={{
                padding: "6px 12px",
                fontSize: "0.8rem",
                background: "#667eea",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <AddIcon style={{ fontSize: "0.8rem" }} />
              追加
            </button>
          )}
        </div>

        {/* ポリシータブ */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            overflowX: "auto",
            paddingBottom: "2px",
          }}
        >
          {editedUseCase.policies && editedUseCase.policies.length > 0 ? (
            editedUseCase.policies.map((policy, index) => (
              <button
                key={index}
                onClick={() => setSelectedPolicyIndex(index)}
                style={{
                  padding: "8px 16px",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  background: selectedPolicyIndex === index ? "#667eea" : "white",
                  color: selectedPolicyIndex === index ? "white" : "#64748b",
                  border: "1px solid #e2e8f0",
                  borderBottom: selectedPolicyIndex === index ? "1px solid #667eea" : "1px solid #e2e8f0",
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                  borderBottomLeftRadius: "0",
                  borderBottomRightRadius: "0",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                  minWidth: "120px",
                  position: "relative",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>{policy.name || `ポリシー ${index + 1}`}</span>
                  {/* レビュー状態インジケーター */}
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: policy.reviewed ? "#22c55e" : "#f59e0b",
                    }}
                  />
                  {/* 変更数バッジ */}
                  {(policy.changes.add_steps.length > 0 || 
                    policy.changes.update_steps.length > 0 || 
                    policy.changes.remove_steps.length > 0) && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        background: selectedPolicyIndex === index ? "rgba(255,255,255,0.2)" : "#e2e8f0",
                        color: selectedPolicyIndex === index ? "white" : "#64748b",
                        padding: "2px 4px",
                        borderRadius: "8px",
                        fontWeight: "600",
                      }}
                    >
                      {policy.changes.add_steps.length + policy.changes.update_steps.length + policy.changes.remove_steps.length}
                    </span>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#9ca3af",
                fontStyle: "italic",
                fontSize: "0.85rem",
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                width: "100%",
              }}
            >
              ポリシーが登録されていません
            </div>
          )}
        </div>
      </div>

      {/* 下部：選択されたポリシーの詳細 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          gap: "16px",
          padding: "16px",
          overflow: "hidden",
        }}
      >
        {selectedPolicyIndex !== null && editedUseCase.policies && editedUseCase.policies[selectedPolicyIndex] ? (
          <>
            {/* ポリシー詳細 */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* サブタブ */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  marginBottom: "16px",
                }}
              >
                <button
                  onClick={() => setActiveSubTab("info")}
                  style={{
                    padding: "12px 20px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    background: activeSubTab === "info" ? "white" : "transparent",
                    color: activeSubTab === "info" ? "#667eea" : "#64748b",
                    border: "none",
                    borderBottom: activeSubTab === "info" ? "2px solid #667eea" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  基本情報
                </button>
                <button
                  onClick={() => setActiveSubTab("steps")}
                  style={{
                    padding: "12px 20px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    background: activeSubTab === "steps" ? "white" : "transparent",
                    color: activeSubTab === "steps" ? "#667eea" : "#64748b",
                    border: "none",
                    borderBottom: activeSubTab === "steps" ? "2px solid #667eea" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  ステップ変更
                </button>
              </div>

              {/* タブコンテンツ */}
              <div style={{ flex: 1, overflow: "hidden" }}>
                {activeSubTab === "info" ? (
                  /* 基本情報タブ */
                  <div
                    style={{
                      padding: "16px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      background: "white",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    {mode === "view" ? (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            {editedUseCase.policies[selectedPolicyIndex].name || `ポリシー ${selectedPolicyIndex + 1}`}
                          </h4>
                          <StatusBadge status={editedUseCase.policies[selectedPolicyIndex].status || "approved"}>
                            {STATUS_LABELS[editedUseCase.policies[selectedPolicyIndex].status || "approved"]}
                          </StatusBadge>
                        </div>
                        <div>
                          <h5 style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#374151" }}>説明</h5>
                          <ViewValue style={{ fontSize: "0.85rem" }}>
                            {editedUseCase.policies[selectedPolicyIndex].description || "説明が入力されていません"}
                          </ViewValue>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
                          <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                            ポリシー名
                          </label>
                          <input
                            value={editedUseCase.policies[selectedPolicyIndex].name}
                            onChange={(e) => onUpdatePolicy(selectedPolicyIndex, "name", e.target.value)}
                            placeholder={`ポリシー ${selectedPolicyIndex + 1}`}
                            style={{
                              width: "100%",
                              fontSize: "1rem",
                              fontWeight: "500",
                              color: "#1e293b",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                              padding: "8px 12px",
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                            説明
                          </label>
                          <textarea
                            value={editedUseCase.policies[selectedPolicyIndex].description || ""}
                            onChange={(e) =>
                              onUpdatePolicy(selectedPolicyIndex, "description", e.target.value)
                            }
                            placeholder="ポリシーの説明を入力してください"
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                              fontSize: "0.85rem",
                              minHeight: "100px",
                              resize: "vertical",
                            }}
                          />
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                              ステータス
                            </label>
                            <select
                              value={editedUseCase.policies[selectedPolicyIndex].status || "approved"}
                              onChange={(e) =>
                                onUpdatePolicy(selectedPolicyIndex, "status", e.target.value as ReviewStatus)
                              }
                              style={{
                                padding: "8px",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                fontSize: "0.875rem",
                              }}
                            >
                              {(Object.keys(STATUS_LABELS) as ReviewStatus[]).map((status) => (
                                <option key={status} value={status}>
                                  {STATUS_LABELS[status]}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={() => {
                              onRemovePolicy(selectedPolicyIndex);
                              // 削除後の選択状態を調整
                              if (editedUseCase.policies && editedUseCase.policies.length > 1) {
                                setSelectedPolicyIndex(Math.max(0, selectedPolicyIndex - 1));
                              } else {
                                setSelectedPolicyIndex(null);
                              }
                            }}
                            style={{
                              padding: "8px 16px",
                              fontSize: "0.8rem",
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            ポリシーを削除
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ステップ変更タブ */
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      height: "100%",
                    }}
                  >
                    {/* 左側：パラメータセクション */}
                    <ParameterSection
                      parameters={editedUseCase.policies[selectedPolicyIndex].parameters || []}
                      usedParameters={usedPolicyParameters}
                      mode={mode}
                      onAddParameter={() => onAddPolicyParameter(selectedPolicyIndex)}
                      onRemoveParameter={(paramIndex) => onRemovePolicyParameter(selectedPolicyIndex, paramIndex)}
                      onUpdateParameter={(paramIndex, field, value) => onUpdatePolicyParameter(selectedPolicyIndex, paramIndex, field, value)}
                      parameterErrors={(() => {
                        // policyParameterErrorsのキーは "policyIndex-paramIndex" 形式
                        // 選択されたポリシーのエラーのみを抽出して、paramIndexをキーとするMapに変換
                        const errors = new Map<number, string>();
                        if (selectedPolicyIndex !== null) {
                          policyParameterErrors.forEach((error, key) => {
                            const [pIndex, paramIndex] = key.split("-").map(Number);
                            if (pIndex === selectedPolicyIndex) {
                              errors.set(paramIndex, error);
                            }
                          });
                        }
                        return errors;
                      })()}
                    />
                    
                    {/* 右側：ステップ詳細 */}
                    <div
                      style={{
                        flex: 1,
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        background: "white",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* コンテンツ部分 */}
                      <div style={{ flex: 1, overflow: "auto", padding: "16px" }}>
                        {mode === "view" ? (
                /* ビューモード時のステップ表示 */
                <div style={{ fontSize: "0.8rem" }}>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#475569",
                      marginBottom: "12px",
                    }}
                  >
                    テストステップ（ポリシー適用後）
                  </div>
                  
                  <div style={{ position: "relative", paddingLeft: "24px" }}>
                    {/* startに追加されるステップ */}
                    {editedUseCase.policies[selectedPolicyIndex].changes.add_steps
                      .filter((step: any) => step.insert_position === "start")
                      .map((step: any, idx: number) => {
                        const {
                          has_target_count_mismatch,
                          target_warning_messages,
                        } = get_target_warning_state(step);

                        return (
                          <div
                            key={`start-${idx}`}
                            style={{
                              padding: "8px 12px",
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              borderRadius: "4px",
                              marginBottom: "6px",
                              fontSize: "0.8rem",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: "-20px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "0.9rem",
                                color: "#059669",
                                fontWeight: "bold",
                              }}
                            >
                              +
                            </div>
                            <div style={{ marginBottom: "4px" }}>
                              <span style={{
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                color: "#059669",
                                marginRight: "8px",
                              }}>
                                [追加]
                              </span>
                              {step.step}
                              {step.action_detail?.step_action?.action &&
                                step.action_detail.step_action.action !== "verify" &&
                                hasTestTargetParameter(
                                  step.action_detail.step_action.action
                                ) &&
                                (!step.action_detail.test_targets ||
                                  step.action_detail.test_targets.length === 0) && (
                                  <span style={{ marginLeft: "8px" }}>
                                    <InvalidTargetBadge>
                                      ターゲット未設定
                                    </InvalidTargetBadge>
                                  </span>
                                )}
                              {step.action_detail &&
                                (hasInvalidTargetsInStep(step) ||
                                  has_target_count_mismatch) && (
                                <span style={{ marginLeft: "8px" }}>
                                  <InvalidTargetBadge>
                                    不正なターゲット
                                  </InvalidTargetBadge>
                                </span>
                              )}
                            </div>
                            {render_target_warning_messages(
                              target_warning_messages,
                              `policy-start-warning-${idx}`
                            )}
                            {step.expect && (
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                期待結果: {step.expect}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    
                    {/* ベースステップと変更内容 */}
                    {editedUseCase.steps.map((baseStep: any, stepIdx: number) => {
                      const isToDelete = editedUseCase.policies[selectedPolicyIndex].changes.remove_steps.some(
                        (rs: any) => rs.step_id === baseStep.step_id
                      );
                      const updateStep = editedUseCase.policies[selectedPolicyIndex].changes.update_steps.find(
                        (us: any) => us.step_id === baseStep.step_id
                      );
                      const displayStep = updateStep || baseStep;
                      const {
                        has_target_count_mismatch: has_display_target_count_mismatch,
                        target_warning_messages: display_target_warning_messages,
                      } = get_target_warning_state(displayStep);
                      const afterSteps = editedUseCase.policies[selectedPolicyIndex].changes.add_steps.filter(
                        (step: any) => step.insert_position === `after:${baseStep.step_id}`
                      );
                      
                      return (
                        <React.Fragment key={baseStep.step_id}>
                          <div
                            style={{
                              padding: "8px 12px",
                              background: isToDelete ? "#fef2f2" : updateStep ? "#fef3c7" : "#f8fafc",
                              border: `1px solid ${
                                isToDelete ? "#fecaca" : updateStep ? "#fcd34d" : "#e2e8f0"
                              }`,
                              borderRadius: "4px",
                              marginBottom: "6px",
                              fontSize: "0.8rem",
                              position: "relative",
                              opacity: isToDelete ? 0.7 : 1,
                              textDecoration: isToDelete ? "line-through" : "none",
                            }}
                          >
                            {isToDelete && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: "-20px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "0.9rem",
                                  color: "#dc2626",
                                  fontWeight: "bold",
                                }}
                              >
                                -
                              </div>
                            )}
                            {updateStep && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: "-20px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "0.9rem",
                                  color: "#f59e0b",
                                  fontWeight: "bold",
                                }}
                              >
                                ↻
                              </div>
                            )}
                            <div style={{ marginBottom: "4px" }}>
                              {isToDelete && (
                                <span style={{
                                  fontSize: "0.7rem",
                                  fontWeight: "500",
                                  color: "#dc2626",
                                  marginRight: "8px",
                                }}>
                                  [削除]
                                </span>
                              )}
                              {updateStep && (
                                <span style={{
                                  fontSize: "0.7rem",
                                  fontWeight: "500",
                                  color: "#f59e0b",
                                  marginRight: "8px",
                                }}>
                                  [更新]
                                </span>
                              )}
                              <span style={{ color: "#64748b", marginRight: "8px" }}>
                                {stepIdx + 1}.
                              </span>
                              {displayStep.step}
                              {displayStep.action_detail?.step_action?.action &&
                                displayStep.action_detail.step_action.action !== "verify" &&
                                hasTestTargetParameter(
                                  displayStep.action_detail.step_action.action
                                ) &&
                                (!displayStep.action_detail.test_targets ||
                                  displayStep.action_detail.test_targets.length === 0) && (
                                  <span style={{ marginLeft: "8px" }}>
                                    <InvalidTargetBadge>
                                      ターゲット未設定
                                    </InvalidTargetBadge>
                                  </span>
                                )}
                              {displayStep.action_detail &&
                                (hasInvalidTargetsInStep(displayStep) ||
                                  has_display_target_count_mismatch) && (
                                <span style={{ marginLeft: "8px" }}>
                                  <InvalidTargetBadge>
                                    不正なターゲット
                                  </InvalidTargetBadge>
                                </span>
                              )}
                            </div>
                            {render_target_warning_messages(
                              display_target_warning_messages,
                              `policy-display-warning-${baseStep.step_id}`
                            )}
                            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                              期待結果: {updateStep ? updateStep.expect : baseStep.expect}
                            </div>
                          </div>
                          
                          {/* このステップの後に追加されるステップ */}
                          {afterSteps.map((step: any, idx: number) => {
                            const {
                              has_target_count_mismatch,
                              target_warning_messages,
                            } = get_target_warning_state(step);

                            return (
                              <div
                                key={`after-${baseStep.step_id}-${idx}`}
                                style={{
                                  padding: "8px 12px",
                                  background: "#f0fdf4",
                                  border: "1px solid #bbf7d0",
                                  borderRadius: "4px",
                                  marginBottom: "6px",
                                  marginLeft: "20px",
                                  fontSize: "0.8rem",
                                  position: "relative",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    left: "-40px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    fontSize: "0.9rem",
                                    color: "#059669",
                                    fontWeight: "bold",
                                  }}
                                >
                                  +
                                </div>
                                <div style={{ marginBottom: "4px" }}>
                                  <span style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "500",
                                    color: "#059669",
                                    marginRight: "8px",
                                  }}>
                                    [追加]
                                  </span>
                                  {step.step}
                                  {step.action_detail?.step_action?.action &&
                                    step.action_detail.step_action.action !== "verify" &&
                                    hasTestTargetParameter(
                                      step.action_detail.step_action.action
                                    ) &&
                                    (!step.action_detail.test_targets ||
                                      step.action_detail.test_targets.length === 0) && (
                                      <span style={{ marginLeft: "8px" }}>
                                        <InvalidTargetBadge>
                                          ターゲット未設定
                                        </InvalidTargetBadge>
                                      </span>
                                    )}
                                  {step.action_detail &&
                                    (hasInvalidTargetsInStep(step) ||
                                      has_target_count_mismatch) && (
                                    <span style={{ marginLeft: "8px" }}>
                                      <InvalidTargetBadge>
                                        不正なターゲット
                                      </InvalidTargetBadge>
                                    </span>
                                  )}
                                </div>
                                {render_target_warning_messages(
                                  target_warning_messages,
                                  `policy-after-warning-${baseStep.step_id}-${idx}`
                                )}
                                {step.expect && (
                                  <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                    期待結果: {step.expect}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* endに追加されるステップ */}
                    {editedUseCase.policies[selectedPolicyIndex].changes.add_steps
                      .filter((step: any) => step.insert_position === "end" || !step.insert_position)
                      .map((step: any, idx: number) => {
                        const {
                          has_target_count_mismatch,
                          target_warning_messages,
                        } = get_target_warning_state(step);

                        return (
                          <div
                            key={`end-${idx}`}
                            style={{
                              padding: "8px 12px",
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              borderRadius: "4px",
                              marginBottom: "6px",
                              fontSize: "0.8rem",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: "-20px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "0.9rem",
                                color: "#059669",
                                fontWeight: "bold",
                              }}
                            >
                              +
                            </div>
                            <div style={{ marginBottom: "4px" }}>
                              <span style={{
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                color: "#059669",
                                marginRight: "8px",
                              }}>
                                [追加]
                              </span>
                              {step.step}
                              {step.action_detail?.step_action?.action &&
                                step.action_detail.step_action.action !== "verify" &&
                                hasTestTargetParameter(
                                  step.action_detail.step_action.action
                                ) &&
                                (!step.action_detail.test_targets ||
                                  step.action_detail.test_targets.length === 0) && (
                                  <span style={{ marginLeft: "8px" }}>
                                    <InvalidTargetBadge>
                                      ターゲット未設定
                                    </InvalidTargetBadge>
                                  </span>
                                )}
                              {step.action_detail &&
                                (hasInvalidTargetsInStep(step) ||
                                  has_target_count_mismatch) && (
                                <span style={{ marginLeft: "8px" }}>
                                  <InvalidTargetBadge>
                                    不正なターゲット
                                  </InvalidTargetBadge>
                                </span>
                              )}
                            </div>
                            {render_target_warning_messages(
                              target_warning_messages,
                              `policy-end-warning-${idx}`
                            )}
                            {step.expect && (
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                期待結果: {step.expect}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  
                  {/* 変更サマリー */}
                  {(editedUseCase.policies[selectedPolicyIndex].changes.add_steps.length > 0 || 
                    editedUseCase.policies[selectedPolicyIndex].changes.update_steps.length > 0 || 
                    editedUseCase.policies[selectedPolicyIndex].changes.remove_steps.length > 0) && (
                    <div style={{ 
                      marginTop: "12px",
                      paddingTop: "12px",
                      borderTop: "1px solid #e2e8f0",
                      fontSize: "0.75rem",
                      color: "#64748b",
                    }}>
                      <strong>変更内容:</strong>
                      {editedUseCase.policies[selectedPolicyIndex].changes.add_steps.length > 0 && (
                        <span style={{ marginLeft: "8px", color: "#059669" }}>
                          +{editedUseCase.policies[selectedPolicyIndex].changes.add_steps.length} 追加
                        </span>
                      )}
                      {editedUseCase.policies[selectedPolicyIndex].changes.update_steps.length > 0 && (
                        <span style={{ marginLeft: "8px", color: "#f59e0b" }}>
                          ↻{editedUseCase.policies[selectedPolicyIndex].changes.update_steps.length} 更新
                        </span>
                      )}
                      {editedUseCase.policies[selectedPolicyIndex].changes.remove_steps.length > 0 && (
                        <span style={{ marginLeft: "8px", color: "#dc2626" }}>
                          -{editedUseCase.policies[selectedPolicyIndex].changes.remove_steps.length} 削除
                        </span>
                      )}
                    </div>
                  )}
                        </div>
                      ) : (
                        /* 編集モード時のBaseStepsList */
                        <BaseStepsList
                          editedUseCase={editedUseCase}
                          setEditedUseCase={setEditedUseCase}
                          policyIndex={selectedPolicyIndex}
                          policy={editedUseCase.policies[selectedPolicyIndex]}
                          localTestTargets={localTestTargets}
                          currentViewId={currentViewId}
                          onRemovePolicyStep={onRemovePolicyStep}
                          onUpdatePolicyStep={onUpdatePolicyStep}
                          onCreateTestTarget={onCreateTestTarget}
                          policyStepValidationErrors={policyStepValidationErrors}
                        />
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#9ca3af",
              fontStyle: "italic",
              fontSize: "0.9rem",
            }}
          >
            ポリシーを選択してください
          </div>
        )}
      </div>
    </div>
  );
};